import React from "react";
import { colors, fonts } from "./theme";
import { Eyebrow } from "./ui";

/**
 * Visible FAQ block.
 *
 * Rendered as plain, always-visible text rather than a collapsed accordion.
 * Two reasons: Google requires FAQPage markup to correspond to content the
 * user can actually see, and AI answer engines quote passages they can read
 * directly — an answer hidden behind a click is a weaker citation candidate.
 *
 * The questions are marked up as <h3> so they sit correctly under the page's
 * single <h1>, which is also what lets a search engine lift one Q&A pair out
 * of the page as a snippet.
 *
 * Whatever is passed here must match the array passed to faqSchema() on the
 * same page. Markup without matching visible text is a policy violation.
 */
export default function FaqSection({
  faqs,
  eyebrow = "Common questions",
  title = "Questions buyers ask",
  style,
}) {
  if (!faqs || !faqs.length) return null;

  return (
    <section style={{ padding: "clamp(40px,9vw,64px) 0 8px", ...style }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2
        style={{
          fontFamily: fonts.serif,
          fontSize: "var(--fs-h2)",
          fontWeight: 500,
          margin: "10px 0 30px",
          maxWidth: "24ch",
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>

      <div
        style={{
          display: "grid",
          // The bare minmax(360px,1fr) kept its 360px minimum even inside a
          // 264px column, so the track — and with it the whole document —
          // ran 96px wider than a 320px screen. Every category page, all 22
          // product pages and /faq scrolled sideways because of this one
          // line. min(360px,100%) caps the floor at the container width.
          gridTemplateColumns: "repeat(auto-fit,minmax(min(360px,100%),1fr))",
          gap: "clamp(22px,5vw,28px) clamp(24px,5vw,40px)",
        }}
      >
        {faqs.map((f) => (
          <div key={f.q}>
            <h3
              style={{
                fontFamily: fonts.serif,
                fontSize: "var(--fs-h4)",
                fontWeight: 600,
                color: colors.ink,
                margin: "0 0 8px",
                lineHeight: 1.35,
              }}
            >
              {f.q}
            </h3>
            <p
              style={{
                // These answers are the passages AI answer engines quote and
                // the longest reading blocks on the site; 14.5px was under
                // the mobile floor.
                fontSize: "var(--fs-body)",
                lineHeight: 1.65,
                color: colors.muted,
                margin: 0,
                maxWidth: "62ch",
              }}
            >
              {f.a}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
